/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { $isLinkNode, LinkNode } from "@lexical/link";
import { BOLD_ITALIC_STAR, BOLD_ITALIC_UNDERSCORE, BOLD_STAR, BOLD_UNDERSCORE, CHECK_LIST, ELEMENT_TRANSFORMERS, INLINE_CODE, ITALIC_STAR, ITALIC_UNDERSCORE, STRIKETHROUGH } from '@lexical/markdown';
import { $createHorizontalRuleNode, $isHorizontalRuleNode, HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { getStyleObjectFromCSS } from "@lexical/selection";
import { $createTableCellNode, $createTableNode, $createTableRowNode, $isTableCellNode, $isTableNode, $isTableRowNode, TableCellHeaderStates, TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { $createAbstractFieldNode, $isAbstractFieldNode, AbstractFieldNode } from "abreact/input/markdown/nodes/AbstractFieldNode";
import { $isImageNode, ImageNode } from 'abreact/input/markdown/nodes/ImageNode';
import { $createPersonNode, $isPersonNode, PersonNode } from "abreact/input/markdown/nodes/PersonNode";
import { convertToMarkdownString } from "abreact/input/markdown/utils/MarkdownExport";
import { convertFromMarkdownString } from "abreact/input/markdown/utils/MarkdownImport";
import { buildInputMarkdown, buildParameterMarkdown, createImageNode, createLinkNode, FIELD_TYPE } from "abreact/input/markdown/utils/MarkdownUtils";
import { $createTextNode, $isParagraphNode, $isTextNode, TextNode } from 'lexical';

// Redeclare the replace function to return the replacement, so we can further process it.

const HR = {
  dependencies: [HorizontalRuleNode],
  export: node => {
    return $isHorizontalRuleNode(node) ? '***' : null;
  },
  regExp: /^(---|\*\*\*|___)\s?$/,
  replace: (parentNode, _1, _2, isImport) => {
    const line = $createHorizontalRuleNode();

    // TODO: Get rid of isImport flag
    if (isImport || parentNode.getNextSibling() != null) {
      parentNode.replace(line);
    } else {
      parentNode.insertBefore(line);
    }
    line.selectNext();
  },
  type: 'element'
};
const IMAGE = {
  dependencies: [ImageNode],
  export: node => {
    if (!$isImageNode(node)) {
      return null;
    }
    let src = node.getSrc();

    // trim the query params from src, if any.
    const questionMarkPos = src.indexOf("?");
    if (questionMarkPos >= 0) {
      src = src.substring(0, questionMarkPos);
    }
    // create the query params from the node's width & height
    const width = node.__width;
    const height = node.__height;
    if (src.startsWith("data:")) {
      // trim the old width and height
      src = src.replace(/width=[^;]*;/, "");
      src = src.replace(/height=[^;]*;/, "");
      // add the new width and height
      if (width !== undefined && height !== undefined) {
        src = src.replace(/png;/, `png;width=${width};height=${height};`);
      }
      return `![${node.getAltText()}](${src})`;
    }
    let queryParams = "";
    if (width !== undefined && height !== undefined) {
      queryParams = "?width=" + width + "&height=" + height;
    }
    return `![${node.getAltText()}](${src}${queryParams})`;
  },
  importRegExp: /!(?:\[([^[]*?)\])(?:\(([^(]+)\))/,
  regExp: /!(?:\[([^[]*?)\])(?:\(([^(]+)\))$/,
  replace: (textNode, match) => {
    const [, altText, src] = match;
    const imageNode = createImageNode(altText, src);
    textNode.replace(imageNode);
    return imageNode;
  },
  trigger: ')',
  type: 'text-match'
};

// Very primitive table setup
const TABLE_ROW_REG_EXP = /^(?:\|)(.+)(?:\|)\s?$/;
const TABLE_ROW_DIVIDER_REG_EXP = /^(\| ?:?-*:? ?)+\|\s?$/;
const TABLE = {
  dependencies: [TableNode, TableRowNode, TableCellNode],
  export: node => {
    if (!$isTableNode(node)) {
      return null;
    }
    const output = [];
    for (const row of node.getChildren()) {
      const rowOutput = [];
      if (!$isTableRowNode(row)) {
        continue;
      }
      let isHeaderRow = false;
      for (const cell of row.getChildren()) {
        // It's TableCellNode so it's just to make flow happy
        if ($isTableCellNode(cell)) {
          rowOutput.push(convertToMarkdownString(AB_MARKDOWN_TRANSFORMERS, cell).replace(/\n/g, '\\n'));
          if (cell.__headerState === TableCellHeaderStates.ROW) {
            isHeaderRow = true;
          }
        }
      }
      output.push(`| ${rowOutput.join(' | ')} |`);
      if (isHeaderRow) {
        output.push(`| ${rowOutput.map(_ => '---').join(' | ')} |`);
      }
    }
    return output.join('\n');
  },
  regExp: TABLE_ROW_REG_EXP,
  replace: (parentNode, _1, match) => {
    // Header row
    if (TABLE_ROW_DIVIDER_REG_EXP.test(match[0])) {
      const table = parentNode.getPreviousSibling();
      if (!table || !$isTableNode(table)) {
        return;
      }
      const rows = table.getChildren();
      const lastRow = rows[rows.length - 1];
      if (!lastRow || !$isTableRowNode(lastRow)) {
        return;
      }

      // Add header state to row cells
      lastRow.getChildren().forEach(cell => {
        if (!$isTableCellNode(cell)) {
          return;
        }
        cell.toggleHeaderStyle(TableCellHeaderStates.ROW);
      });

      // Remove line
      parentNode.remove();
      return;
    }
    const matchCells = mapToTableCells(match[0]);
    if (matchCells == null) {
      return;
    }
    const rows = [matchCells];
    let sibling = parentNode.getPreviousSibling();
    let maxCells = matchCells.length;
    while (sibling) {
      if (!$isParagraphNode(sibling)) {
        break;
      }
      if (sibling.getChildrenSize() !== 1) {
        break;
      }
      const firstChild = sibling.getFirstChild();
      if (!$isTextNode(firstChild)) {
        break;
      }
      const cells = mapToTableCells(firstChild.getTextContent());
      if (cells == null) {
        break;
      }
      maxCells = Math.max(maxCells, cells.length);
      rows.unshift(cells);
      const previousSibling = sibling.getPreviousSibling();
      sibling.remove();
      sibling = previousSibling;
    }
    const table = $createTableNode();
    for (const cells of rows) {
      const tableRow = $createTableRowNode();
      table.append(tableRow);
      for (let i = 0; i < maxCells; i++) {
        tableRow.append(i < cells.length ? cells[i] : createTableCell(''));
      }
    }
    const previousSibling = parentNode.getPreviousSibling();
    if ($isTableNode(previousSibling) && getTableColumnsSize(previousSibling) === maxCells) {
      previousSibling.append(...table.getChildren());
      parentNode.remove();
    } else {
      parentNode.replace(table);
    }
    table.selectEnd();
  },
  type: 'element'
};
function getTableColumnsSize(table) {
  const row = table.getFirstChild();
  return $isTableRowNode(row) ? row.getChildrenSize() : 0;
}
const createTableCell = textContent => {
  textContent = textContent.replace(/\\n/g, '\n');
  const cell = $createTableCellNode(TableCellHeaderStates.NO_STATUS);
  convertFromMarkdownString(textContent, AB_MARKDOWN_TRANSFORMERS, cell);
  return cell;
};
const mapToTableCells = textContent => {
  const match = textContent.match(TABLE_ROW_REG_EXP);
  if (!match || !match[1]) {
    return null;
  }
  return match[1].split('|').map(text => createTableCell(text));
};
const COLOR = {
  dependencies: [TextNode],
  export: (node, exportChildren, exportFormat) => {
    if (!$isTextNode(node)) {
      return null;
    }
    const style = node.getStyle();
    // Look for either the color or background-color styles.
    let color = getStyleObjectFromCSS(style)["color"];
    let highlight = getStyleObjectFromCSS(style)["background-color"];
    if (!color && !highlight) {
      // no color or highlight in the style?  No change.
      return null;
    }
    let newContent = exportFormat(node, node.getTextContent());
    if (color) {
      const matchArray = color.match(/var\(--ab-global-color-([a-z]+-[0-9]+)\)/);
      if (matchArray) {
        // is the color an ab initio built-in color, like "var(--ab-global-color-red-500)" ?
        color = matchArray[1];
      }
      newContent = `{color:${color}}${newContent}{color}`;
    }
    if (highlight) {
      const matchArray = highlight.match(/var\(--ab-global-color-([a-z]+-[0-9]+)\)/);
      if (matchArray) {
        // is the color an ab initio built-in color, like "var(--ab-global-color-red-500)" ?
        highlight = matchArray[1];
      }
      newContent = `{background:${highlight}}${newContent}{background}`;
    }
    return newContent;
  },
  importRegExp: /{(color)\s*:\s*([^}]*)}(.*?){color}|{background\s*:\s*([^}]*)}(.*?){background}/,
  regExp: /{color\s*:\s*([^}]*)}(.*?){color}|{background\s*:\s*([^}]*)}(.*?){background}/,
  replace: (textNode, match) => {
    // split out the capturing groups to nice names.
    const [, isColor, col1, text1, col2, text2] = match;
    let background;
    let color;
    let text;
    if (isColor) {
      // match on first half
      color = col1;
      text = text1;
    } else {
      // match on second half
      background = col2;
      text = text2;
    }

    // TODO: this only works if color & highlight are completely nested. What about partially nested?
    const innerMatch = text.match(COLOR.importRegExp);
    if (innerMatch) {
      // split out the capturing groups to nice names
      const [, innerIsCol, innerCol1, innerText1, innerCol2, innerText2] = innerMatch;
      if (innerIsCol) {
        color = innerCol1;
        text = innerText1;
      } else {
        background = innerCol2;
        text = innerText2;
      }
    }
    const newTextNode = $createTextNode(text);
    if (color?.match(/^[a-z]+-[0-9]+$/)) {
      // Is the color an ab initio built-in color, like "red-500" ?
      color = `var(--ab-global-color-${color})`;
    }
    if (background?.match(/^[a-z]+-[0-9]+$/)) {
      // Is the background an ab initio built-in color, like "red-500" ?
      background = `var(--ab-global-color-${background})`;
    }
    // put the calculated colors into the textNode's styles.
    newTextNode.setStyle((color ? `color:${color};` : "") + (background ? `background-color:${background};` : ""));
    // ABSDK-4348: if the original has a format, copy it
    newTextNode.setFormat(textNode.getFormat());
    textNode.replace(newTextNode);
    return newTextNode;
  },
  trigger: '}',
  type: 'text-match'
};
const PERSON = {
  dependencies: [PersonNode],
  export: (node, exportChildren, exportFormat) => {
    if (!$isPersonNode(node)) {
      return null;
    }
    const id = node.getId();
    const displayName = node.getDisplayName();
    return `[${displayName}](person:${id})`;
  },
  importRegExp: /(?:\[([^[]+)\])(?:\((?:(person:[^()]+)(?:\s"((?:[^"]*\\")*[^"]*)"\s*)?)\))/,
  regExp: /(?:\[([^[]+)\])(?:\((?:(person:[^()]+)(?:\s"((?:[^"]*\\")*[^"]*)"\s*)?)\))$/,
  replace: (textNode, match) => {
    const [, displayName, linkUrl] = match;
    const id = linkUrl.substring(7);
    const personNode = $createPersonNode(id, displayName);
    textNode.replace(personNode);
    return personNode;
  },
  trigger: ')',
  type: "text-match"
};
const FIELD = {
  dependencies: [AbstractFieldNode],
  export: (node, exportChildren, exportFormat) => {
    if (!$isAbstractFieldNode(node)) {
      return null;
    }
    if (!node.getIsGeneral()) {
      return null;
    }
    const techName = node.getTechName();
    const displayName = node.getDisplayName();
    return `[${displayName}](field:${techName})`;
  },
  importRegExp: /(?:\[([^[]+)\])(?:\((?:(field:[^()]+)(?:\s"((?:[^"]*\\")*[^"]*)"\s*)?)\))/,
  regExp: /(?:\[([^[]+)\])(?:\((?:(field:[^()]+)(?:\s"((?:[^"]*\\")*[^"]*)"\s*)?)\))$/,
  replace: (textNode, match) => {
    const [, displayName, linkUrl] = match;
    const techName = linkUrl.substring(6);
    const fieldNode = $createAbstractFieldNode({
      displayName: displayName,
      fieldDef: {
        techName: techName,
        businessName: displayName,
        canonicalName: techName,
        typeIcon: "generic"
      },
      type: FIELD_TYPE.NONE
    });
    textNode.replace(fieldNode);
    return fieldNode;
  },
  trigger: ')',
  type: "text-match"
};
const INPUT = {
  dependencies: [AbstractFieldNode],
  export: (node, exportChildren, exportFormat) => {
    if (!$isAbstractFieldNode(node)) {
      return null;
    }
    if (!node.getIsInput()) {
      return null;
    }
    const canonicalName = node.getCanonicalName();
    const displayName = node.getDisplayName();
    const businessName = node.getBusinessName();
    const typeIcon = node.getTypeIcon();
    const breEncoding = node.getBreEncoding();
    return buildInputMarkdown(displayName, canonicalName, businessName, breEncoding, typeIcon);
  },
  importRegExp: /(?:\[([^[]+)\])(?:\((?:(input:[^()]+)(?:\s"((?:[^"]*\\")*[^"]*)"\s*)?)\))/,
  regExp: /(?:\[([^[]+)\])(?:\((?:(input:[^()]+)(?:\s"((?:[^"]*\\")*[^"]*)"\s*)?)\))$/,
  replace: (textNode, match) => {
    const [, displayName, linkUrl] = match;
    const body = linkUrl.substring(6).split(":");
    const canonicalName = body[0];
    const techName = canonicalName.split(".").pop() ?? "";
    const typeIcon = body[3];
    const businessName = body[1];
    const breEncoding = body[2];
    const fieldNode = $createAbstractFieldNode({
      displayName: displayName,
      fieldDef: {
        techName: techName,
        businessName: businessName,
        canonicalName: canonicalName,
        typeIcon: typeIcon,
        breEncoding: breEncoding
      },
      type: FIELD_TYPE.INPUT
    });
    textNode.replace(fieldNode);
    return fieldNode;
  },
  trigger: ')',
  type: "text-match"
};
const PARAM = {
  dependencies: [AbstractFieldNode],
  export: (node, exportChildren, exportFormat) => {
    if (!$isAbstractFieldNode(node)) {
      return null;
    }
    if (!node.getIsParam()) {
      return null;
    }
    const techName = node.getTechName();
    const displayName = node.getDisplayName();
    const typeIcon = node.getTypeIcon();
    return buildParameterMarkdown(techName, displayName, typeIcon);
  },
  importRegExp: /(?:\[([^[]+)\])(?:\((?:(param:[^()]+)(?:\s"((?:[^"]*\\")*[^"]*)"\s*)?)\))/,
  regExp: /(?:\[([^[]+)\])(?:\((?:(param:[^()]+)(?:\s"((?:[^"]*\\")*[^"]*)"\s*)?)\))$/,
  replace: (textNode, match) => {
    const [, displayName, linkUrl] = match;
    const body = linkUrl.substring(6).split(":");
    const techName = body[0];
    const typeIcon = body[1];
    const fieldNode = $createAbstractFieldNode({
      displayName: displayName,
      type: FIELD_TYPE.PARAM,
      fieldDef: {
        techName: techName,
        typeIcon: typeIcon
      }
    });
    textNode.replace(fieldNode);
    return fieldNode;
  },
  trigger: ')',
  type: "text-match"
};
const AB_LINK = {
  dependencies: [LinkNode],
  export: (node, exportChildren, exportFormat) => {
    if (!$isLinkNode(node)) {
      return null;
    }
    const title = node.getTitle();
    // Markdown doesn't support formatting inside a link name, so we know it's all text.
    // TODO: what about image children?
    const linkContent = title ? `[${node.getTextContent()}](${node.getURL()} "${title}")` : `[${node.getTextContent()}](${node.getURL()})`;
    // const firstChild = node.getFirstChild();
    // if (node.getChildrenSize() === 1 && $isTextNode(firstChild)) {
    //   return exportFormat(firstChild, linkContent);
    // } else {
    return linkContent;
    // }
  },
  importRegExp: /(?<!!)\[([^[]*?)]\(([^()\s]+)(?:\s"((?:[^"]*?\\")*?[^"]*?)"\s*?)?\)/,
  regExp: /(?<!!)\[([^[]*?)]\(([^()\s]+)(?:\s"((?:[^"]*?\\")*?[^"]*?)"\s*?)?\)$/,
  replace: (textNode, match) => {
    const [, linkText, linkUrl, linkTitle] = match;
    const linkNode = createLinkNode(linkText === "" ? "_" : linkText, linkUrl, linkTitle, textNode);
    textNode.replace(linkNode);
    return linkNode;
  },
  trigger: ')',
  type: 'text-match'
};
const UNDERLINE = {
  format: ['underline'],
  tag: "++",
  intraword: false,
  // must be here because embedded images encoded in base64 will contain "++".
  type: "text-format"
};

// Make our own version of text format transformers.
// We add UNDERLINE, move ITALIC_UNDERSCORE ahead of ITALIC_STAR, and remove HIGHLIGHT
const TEXT_FORMAT_TRANSFORMERS = [INLINE_CODE, BOLD_ITALIC_STAR, BOLD_ITALIC_UNDERSCORE, BOLD_STAR, BOLD_UNDERSCORE, ITALIC_UNDERSCORE, ITALIC_STAR, STRIKETHROUGH, UNDERLINE];
export const AB_MARKDOWN_TRANSFORMERS = [COLOR, TABLE, HR, IMAGE, CHECK_LIST, ...ELEMENT_TRANSFORMERS, ...TEXT_FORMAT_TRANSFORMERS, PERSON, FIELD, INPUT, PARAM, /* ...TEXT_MATCH_TRANSFORMERS */ // taken out because LINK is replaced by AB_LINK
AB_LINK];
//# sourceMappingURL=MarkdownTransformers.js.map
